/* eslint-disable no-nested-ternary */
import React, { useState } from 'react';
import { CloseOutlined, PlusOutlined, SyncOutlined } from '@ant-design/icons';
import { useIsMutating } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { booleanOptions, questionValueOptions } from '@app/constants';
import { QuestionOption, useHandleFormErrors, useQuestionsList, useToggleState } from '@app/utils';
import {
  Namespace,
  useCreateQuestion,
  useEditCriteria,
  useICDCodesList,
  usePermissions,
} from '@data/index';
import {
  AnswerType,
  CriteriaType,
  CriteriaUpdateBody,
  QuestionBody,
  QuestionCriterionView,
  QuestionType,
  TrialCriteriaView,
  VisibilityType,
} from '@data/sdk/match';
import {
  Button,
  AntdCol,
  AntdForm,
  AntdInput,
  AntdRadio,
  AntdRow,
  AntdSelect,
  AntdSpace,
  AntdTypography,
  antdNotification,
  AntdDrawer,
} from '@ui';
import { EditCriteriaFormValues, useFormData } from './hooks';

const { Text } = AntdTypography;
const { TextArea } = AntdInput;

interface EditCriteriaProps {
  criteria:
    | TrialCriteriaView
    | (QuestionCriterionView & {
        answerType: AnswerType;
        questionId: string;
        questionText: string;
      });
  handleClose: () => void;
  invalidateQuestionCriteria?: () => void;
  isAdmin: boolean;
  isOpen: boolean;
  isQuestionCriteria?: boolean;
}

export const EditCriteria = ({
  isOpen,
  handleClose,
  criteria,
  invalidateQuestionCriteria,
  isQuestionCriteria = false,
}: EditCriteriaProps) => {
  const { t } = useTranslation([Namespace.ADMIN]);
  const isMutating = !!useIsMutating();

  const [createCustomQuestion, setCreateCustomQuestion] = useState(false);
  const [showSuggestedQuestions, toggleShowSuggestedQuestions] = useToggleState(true);

  const { IS_REVIEWER } = usePermissions();
  const { mutate: editCriteria } = useEditCriteria();
  const { mutate: createCriteriaQuestion } = useCreateQuestion();
  const { form, question, answerType, initialValues } = useFormData(criteria);
  const { questionOptions, questionsLoading, searchText, setSearchText, selectOptions } =
    useQuestionsList();
  const { icdCodesOptions, fetchNextPage, isLoading, handleSearchChange } = useICDCodesList();

  const onClose = () => {
    form.resetFields();
    setSearchText('');
    setCreateCustomQuestion(false);
    handleClose();
  };

  const handleClearAnswerFields = () => {
    form.setFieldsValue({
      value: null,
      minThreshold: null,
      maxThreshold: null,
      unit: null,
      answerType: undefined,
    });
  };

  const handleReplaceQuestion = (value: QuestionOption) => {
    setCreateCustomQuestion(false);
    form.setFieldValue('question', value);
    form.setFieldValue('answerType', value.answerType);
    form.setFieldValue('unit', value.unit);
  };

  const handleSelectQuestion = (value: QuestionOption) => {
    handleClearAnswerFields();
    form.setFieldValue('question', value);
    setCreateCustomQuestion(!!value.createOwn);
    if (!value.createOwn) {
      form.setFieldValue('answerType', value.answerType);
      form.setFieldValue('unit', value.unit);
    }
  };

  const handleClearSelect = () => {
    setSearchText('');
    handleClearAnswerFields();
    setCreateCustomQuestion(false);
  };

  const handleEditCriteria = (values: CriteriaUpdateBody) => {
    editCriteria(
      {
        criteriaId: isQuestionCriteria
          ? (criteria as QuestionCriterionView).criteriaId
          : criteria.id,
        values,
      },
      {
        onSuccess: () => {
          handleClose();
          antdNotification.success({ message: t('trial_page.criteria_successfully_edited') });
          if (isQuestionCriteria && invalidateQuestionCriteria) {
            invalidateQuestionCriteria();
          }
        },
        onError: (err) => useHandleFormErrors(err, form),
      },
    );
  };

  const onFinish = (formValues: EditCriteriaFormValues) => {
    const editCriteriaValues: CriteriaUpdateBody = {
      originalCriteriaText: formValues.originalCriteriaText,
      value: formValues.value ?? undefined,
      maxThreshold: formValues.maxThreshold ? Number(formValues.maxThreshold) : undefined,
      minThreshold: formValues.minThreshold ? Number(formValues.minThreshold) : undefined,
      questionId: formValues.question.value,
      otherValues: formValues.otherValues ?? [],
    };

    const createQuestionValues: QuestionBody = {
      answerType: formValues.answerType,
      text: searchText,
      unit: formValues.unit ?? undefined,
      questionType: QuestionType.Standard,
      visibilityType: VisibilityType.Default,
      autoGenerateTranslations: true,
    };

    if (createCustomQuestion) {
      createCriteriaQuestion(createQuestionValues, {
        onSuccess: (data) => handleEditCriteria({ ...editCriteriaValues, questionId: data.id }),
        onError: ({ response }) => {
          antdNotification.error({
            message: t(`patient:beErrors.${response?.data.message}`, {
              defaultValue: t('patient:beErrors.unexpectedError'),
              item: 'Question',
            }),
          });
        },
      });
    } else {
      handleEditCriteria(editCriteriaValues);
    }
  };

  const handleScroll = (e: any) => {
    if (e.target.scrollTop + e.target.offsetHeight + 2 > e.target.scrollHeight) {
      fetchNextPage();
    }
  };

  const showSuggestedList =
    createCustomQuestion && showSuggestedQuestions && !!questionOptions.length;

  return (
    <AntdDrawer
      closeIcon={null}
      onClose={onClose}
      open={isOpen}
      title={t('trial_page.edit_criteria')}
      width={600}
      extra={<CloseOutlined onClick={onClose} className="text-base-700" />}
      styles={{
        header: { borderBottom: 'none' },
        body: { paddingTop: 0 },
      }}
      destroyOnClose
    >
      <AntdForm
        form={form}
        layout="vertical"
        autoComplete="off"
        requiredMark={false}
        onFinish={onFinish}
        className="[&>div]:mb-4"
        scrollToFirstError
        initialValues={initialValues}
      >
        <AntdForm.Item name="type" label={t('trial_page.original_trial_criteria')}>
          <AntdRadio.Group>
            <AntdRadio
              value={CriteriaType.Inclusion}
              disabled={criteria.type !== CriteriaType.Inclusion}
            >
              {t('trial_page.inclusion')}
            </AntdRadio>
            <AntdRadio
              value={CriteriaType.Exclusion}
              disabled={criteria.type !== CriteriaType.Exclusion}
            >
              {t('trial_page.exclusion')}
            </AntdRadio>
          </AntdRadio.Group>
        </AntdForm.Item>

        <AntdForm.Item
          name="originalCriteriaText"
          rules={[{ required: true, message: t('patient:validations.required') }]}
        >
          <TextArea
            rows={5}
            className="w-full rounded-default"
            maxLength={250}
            style={{ resize: 'none' }}
          />
        </AntdForm.Item>

        <AntdForm.Item
          name="question"
          label={t('trial_page.question')}
          rules={[{ required: true, message: t('patient:validations.required') }]}
        >
          <AntdSelect<any, QuestionOption>
            showSearch
            value={selectOptions?.find((el) => el?.value === question?.value) ?? searchText}
            placeholder={t('patient:common.type_here')}
            filterOption={false}
            onSearch={setSearchText}
            notFoundContent={null}
            loading={questionsLoading}
            allowClear
            dropdownStyle={{ padding: '0 0 4px' }}
            listHeight={500}
            onSelect={(_, option) => handleSelectQuestion(option)}
            onClear={handleClearSelect}
            optionLabelProp="label"
            disabled={!!initialValues.otherValues}
          >
            {selectOptions.map((el) => (
              <AntdSelect.Option
                className={`${el.info ? 'text-base-600 bg-base-200' : 'mx-1'} ${
                  el.createOwn ? 'text-primary-600' : ''
                }`}
                {...el}
                key={el.value}
                disabled={el.info}
              >
                {el.createOwn ? (
                  <>
                    <PlusOutlined /> {t('patient:common.add')} {`"${el.label}"`}
                  </>
                ) : (
                  el.label
                )}
              </AntdSelect.Option>
            ))}
          </AntdSelect>
          {showSuggestedList && (
            <AntdRow>
              <AntdSpace
                direction="vertical"
                size={12}
                className="w-full mt-1 p-4 rounded-lg bg-primary-200 "
              >
                <Text>{t('trial_page.check_if_there_question_below')}:</Text>
                <ul className="p-0 m-0">
                  {questionOptions.slice(0, 3).map((el) => (
                    <li
                      key={el.value}
                      className="list-none p-0 py-[1px] flex justify-between items-center border-solid border-0 border-t [&:last-child]:border-b border-base-300"
                    >
                      <Text className="font-medium">{el.label}</Text>{' '}
                      <Button
                        icon={<SyncOutlined />}
                        className="text-primary-600 px-0"
                        type="text"
                        onClick={() => handleReplaceQuestion(el)}
                      >
                        {t('patient:common.replace')}
                      </Button>
                    </li>
                  ))}
                </ul>
                <Button
                  className="bg-transparent font-medium shadow-none"
                  onClick={toggleShowSuggestedQuestions}
                >
                  {t('trial_page.ignore_this_list')}
                </Button>
              </AntdSpace>
            </AntdRow>
          )}
        </AntdForm.Item>

        <AntdRow justify="start" className="gap-6">
          {!initialValues.otherValues && (
            <AntdCol xs={10}>
              <AntdForm.Item
                name="answerType"
                label={t('trial_page.value')}
                rules={[{ required: true, message: t('patient:validations.required') }]}
                className="mb-0"
              >
                <AntdSelect
                  options={questionValueOptions()}
                  className="max-w-[240px]"
                  onChange={() =>
                    form.setFieldsValue({
                      value: null,
                      minThreshold: null,
                      maxThreshold: null,
                      unit: null,
                    })
                  }
                  disabled={!createCustomQuestion}
                />
              </AntdForm.Item>
            </AntdCol>
          )}

          {initialValues.otherValues && (
            <AntdCol xs={10}>
              <AntdForm.Item
                name="otherValues"
                label={t('trial_page.expected_answer')}
                rules={[{ required: true, message: t('patient:validations.please_select') }]}
              >
                {/* <AntdSelect
                  options={diseaseOptions}
                  mode="multiple"
                  showSearch
                  loading={diseasesLoading}
                /> */}
                <AntdSelect
                  mode="multiple"
                  showSearch
                  size="large"
                  options={icdCodesOptions}
                  filterOption={false}
                  loading={isLoading}
                  onPopupScroll={handleScroll}
                  onSearch={handleSearchChange}
                />
              </AntdForm.Item>
            </AntdCol>
          )}
          {answerType === AnswerType.Boolean && (
            <AntdCol xs={10}>
              <AntdForm.Item
                name="value"
                label={t('trial_page.expected_answer')}
                rules={[
                  {
                    required: answerType === AnswerType.Boolean,
                    message: t('patient:validations.please_select'),
                  },
                ]}
              >
                <AntdSelect options={booleanOptions()} />
              </AntdForm.Item>
            </AntdCol>
          )}
        </AntdRow>

        {answerType === AnswerType.Number && (
          <AntdRow justify="space-between" className="flex gap-6 flex-nowrap">
            <AntdCol>
              <AntdForm.Item
                name="minThreshold"
                dependencies={['maxThreshold']}
                label={t('trial_page.min_threshold')}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value && !getFieldValue('maxThreshold')) {
                        return Promise.reject(new Error(t('patient:validations.required')));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
                normalize={(v) => v.replace(/\D/g, '')}
              >
                <AntdInput />
              </AntdForm.Item>
            </AntdCol>
            <AntdCol>
              <AntdForm.Item
                name="maxThreshold"
                dependencies={['minThreshold']}
                label={t('trial_page.max_threshold')}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value && !getFieldValue('minThreshold')) {
                        return Promise.reject(new Error(t('patient:validations.required')));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
                normalize={(v) => v.replace(/\D/g, '')}
              >
                <AntdInput />
              </AntdForm.Item>
            </AntdCol>
            <AntdCol>
              <AntdForm.Item name="unit" label={t('trial_page.unit')}>
                <AntdInput disabled={!createCustomQuestion} />
              </AntdForm.Item>
            </AntdCol>
          </AntdRow>
        )}
        {!IS_REVIEWER && (
          <AntdSpace direction="horizontal" className="w-full mt-2">
            <Button type="primary" htmlType="submit" loading={isMutating}>
              {t('trial_page.edit_criteria')}
            </Button>
            <Button onClick={onClose} disabled={isMutating}>
              {t('patient:common.cancel')}
            </Button>
          </AntdSpace>
        )}
      </AntdForm>
    </AntdDrawer>
  );
};
